/**
 * VersionTimeline — Visual version history for Quotes, Contracts, and Change Orders.
 * Shows the lineage of commercial state with clear current/original distinction.
 */

import { useMemo } from "react";
import type { VersionLineage, VersionRecord } from "@/data/versioningSystem";
import { getActiveVersion, getOriginalVersion } from "@/data/versioningSystem";
import { Badge } from "@/components/ui/badge";
import {
  FileText, CheckCircle, Clock, Pen, AlertTriangle,
  ArrowRight, Shield, Ban, GitBranch,
} from "lucide-react";

interface Props {
  lineage: VersionLineage;
  compact?: boolean;
  /** If provided, clicking a version calls this */
  onSelectVersion?: (version: VersionRecord) => void;
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  quote: Pen,
  contract: Shield,
  change_order: GitBranch,
  job_update: ArrowRight,
};

const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
  draft: { badge: "bg-muted text-muted-foreground", dot: "bg-muted-foreground" },
  active: { badge: "bg-primary/10 text-primary", dot: "bg-primary" },
  signed: { badge: "bg-success/10 text-success", dot: "bg-success/60" },
  superseded: { badge: "bg-warning/10 text-warning", dot: "bg-warning/40" },
  voided: { badge: "bg-destructive/10 text-destructive", dot: "bg-destructive" },
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  active: "Active",
  signed: "Signed",
  superseded: "Previous",
  voided: "Voided",
};

export function VersionTimeline({ lineage, compact, onSelectVersion }: Props) {
  const activeVersion = useMemo(() => getActiveVersion(lineage), [lineage]);
  const originalVersion = useMemo(() => getOriginalVersion(lineage), [lineage]);

  if (lineage.versions.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic py-3">
        No version history available
      </div>
    );
  }

  if (compact) {
    return <CompactView lineage={lineage} activeVersion={activeVersion} />;
  }

  return (
    <div className="space-y-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em]">Version History</p>
        <Badge variant="outline" className="text-[9px] h-5">
          {lineage.versions.length} version{lineage.versions.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Timeline */}
      <div className="relative">
        {lineage.versions.map((version, i) => {
          const isActive = activeVersion?.id === version.id;
          const isOriginal = originalVersion?.id === version.id;
          const isLast = i === lineage.versions.length - 1;
          const Icon = TYPE_ICONS[version.type] || FileText;
          const style = STATUS_STYLES[version.status] || STATUS_STYLES.draft;

          return (
            <div
              key={version.id}
              className={`relative flex gap-3 pb-4 ${onSelectVersion ? "cursor-pointer hover:bg-muted/30 -mx-2 px-2 rounded-lg transition-colors" : ""}`}
              onClick={() => onSelectVersion?.(version)}
            >
              {/* Timeline connector */}
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isActive ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""} ${style.badge}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                {!isLast && (
                  <div className="w-px flex-1 bg-border mt-1" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-xs font-semibold ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                    {version.label}
                  </p>
                  <Badge className={`text-[9px] h-4 ${style.badge} border-0`}>
                    {isActive ? "Current" : isOriginal && version.status === "superseded" ? "Original" : STATUS_LABELS[version.status]}
                  </Badge>
                  {version.version && (
                    <span className="text-[9px] text-muted-foreground">v{version.version}</span>
                  )}
                </div>

                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{version.summary}</p>

                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                  <span className="font-semibold text-foreground">${version.contractValue.toLocaleString()}</span>
                  {version.delta !== undefined && version.delta !== 0 && (
                    <span className={version.delta > 0 ? "text-success" : "text-destructive"}>
                      {version.delta > 0 ? "+" : ""}${version.delta.toLocaleString()}
                    </span>
                  )}
                  <span>·</span>
                  <span>{new Date(version.createdAt).toLocaleDateString()}</span>
                  {version.signedAt && (
                    <>
                      <span>·</span>
                      <span className="flex items-center gap-0.5">
                        <CheckCircle className="h-2.5 w-2.5 text-success" />
                        Signed {new Date(version.signedAt).toLocaleDateString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Compact version (for sidebars, cards) ─── */

function CompactView({ lineage, activeVersion }: { lineage: VersionLineage; activeVersion: VersionRecord | null }) {
  const signedCount = lineage.versions.filter(v => v.status === "signed" || v.status === "superseded").length;
  const changeOrders = lineage.versions.filter(v => v.type === "change_order").length;

  return (
    <div className="flex items-center gap-3 text-xs">
      {/* Current version indicator */}
      {activeVersion && (
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-success/60" />
          <span className="font-medium">v{activeVersion.version}</span>
          <span className="text-muted-foreground">${activeVersion.contractValue.toLocaleString()}</span>
        </div>
      )}

      {/* History summary */}
      <div className="flex items-center gap-2 text-muted-foreground">
        {signedCount > 1 && (
          <span className="flex items-center gap-0.5">
            <Clock className="h-3 w-3" />
            {signedCount} versions
          </span>
        )}
        {changeOrders > 0 && (
          <span className="flex items-center gap-0.5">
            <GitBranch className="h-3 w-3" />
            {changeOrders} CO{changeOrders !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Original vs Current comparison banner ─── */

export function VersionComparisonBanner({ lineage }: { lineage: VersionLineage }) {
  const active = getActiveVersion(lineage);
  const original = getOriginalVersion(lineage);

  if (!active || !original || active.id === original.id) return null;

  const delta = active.contractValue - original.contractValue;

  return (
    <div className="rounded-lg border bg-muted/20 p-3 flex items-center justify-between text-xs">
      <div className="flex items-center gap-4">
        <div>
          <p className="text-muted-foreground">Original Contract</p>
          <p className="font-semibold">${original.contractValue.toLocaleString()}</p>
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
        <div>
          <p className="text-muted-foreground">Current Approved</p>
          <p className="font-semibold">${active.contractValue.toLocaleString()}</p>
        </div>
      </div>
      <Badge variant="outline" className={`text-[10px] ${delta > 0 ? "text-success border-success/30" : delta < 0 ? "text-destructive border-destructive/20" : ""}`}>
        {delta > 0 ? "+" : ""}{delta !== 0 ? `$${delta.toLocaleString()}` : "No Change"}
        {lineage.versions.filter(v => v.type === "change_order").length > 0 && (
          <span className="ml-1 text-muted-foreground">
            · {lineage.versions.filter(v => v.type === "change_order").length} CO{lineage.versions.filter(v => v.type === "change_order").length !== 1 ? "s" : ""}
          </span>
        )}
      </Badge>
    </div>
  );
}
